<template>
    <div id="app">
        <check-version />
        <template v-if="isLoadingEnd">
            <template v-if="userHasAccess">
                <template v-if="isDesktopOrTablet">
                    <support-button class="support" />
                    <environment />
                </template>
                <top-menu
                    :shortMenu="shortMenu"
                    :isAuthenticated="isAuthenticated"
                />
                <b-container
                    fluid
                    class="container-main content"
                >
                    <breadcrumb style="display: none" />
                    <div
                        v-if="loading"
                        class="text-center"
                    >
                        <b-spinner class="align-middle" />
                    </div>
                    <b-row
                        v-else
                        class="sidebar"
                    >
                        <b-col
                            v-if="isAuthenticated && isDesktopOrTablet"
                            :md="shortMenu ? 1 : 2"
                            class="px-0 overflow-hidden"
                        >
                            <sidebar-menu :isShort.sync="shortMenu" />
                        </b-col>
                        <b-col
                            :md="isAuthenticated ? (shortMenu ? 11 : 10) : 12"
                            class="pt-4 pt-md-3 mb-md-3"
                        >
                            <h3 class="heading mb-5">
                                <strong>{{ title }}</strong>
                                <span
                                    v-if="mainSubtitle"
                                    class="main-subtitle"
                                >
                                    {{ mainSubtitle }}
                                </span>
                            </h3>
                            <router-view v-if="!isPageHasErrors" />
                            <error
                                v-else
                                :errorCode="+currentErrorCode"
                            />
                            <exception />
                            <modal-update-page />
                        </b-col>
                    </b-row>
                </b-container>
                <banners v-if="isDesktopOrTablet" />
            </template>
            <access-denied v-else />
        </template>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import Exception from './components/Exception';
import Error from './components/errors/Error';
import access from './mixins/access';
import SidebarMenu from './components/SidebarMenu';
import TopMenu from './components/topMenu/TopMenu';
import CheckVersion from './components/CheckVersion';
import Breadcrumb from './Breadcrumb';
import SupportButton from './components/SupportButton';
import Environment from './components/Environment';
import ModalUpdatePage from './components/ModalUpdatePage';
import { keycloak } from './services/keycloak';
import window from '@/mixins/window';
import { ERROR_CODES } from '@/helpers/constants';

export default {
    name: 'App',
    components: {
        SupportButton,
        Breadcrumb,
        CheckVersion,
        TopMenu,
        SidebarMenu,
        Exception,
        Error,
        Environment,
        ModalUpdatePage,
        Banners: () => import('@/components/banners'),
        AccessDenied: () => import('@/components/errors/AccessDenied/index'),
    },

    provide: function() {
        return {
            logout: this.logout,
        };
    },

    mixins: [access, window],

    data() {
        return {
            h1: '',
            mainSubtitle: '',
            pageNotFound: false,
            menuList: [],
            loading: false,
            shortMenu: false,
            orderIsNotAvailableMobile: false,
        };
    },

    computed: {
        isAuthenticated: function() {
            return this.$store.getters.isAuthenticated;
        },

        title: function() {
            if (this.h1 !== null) {
                return this.h1;
            }

            if (this.isMobile && this.$route.meta.titleMobile) {
                return this.$t(`main.header.${this.$route.meta.titleMobile}`);
            } else {
                const PLURALIZATION_CHOICE = 2;
                return this.$route.meta.title ? this.$tc(`main.header.${this.$route.meta.title}`, PLURALIZATION_CHOICE) : '';
            }
        },

        currentErrorCode() {
            switch (true) {
                case this.pageNotFound:
                    return ERROR_CODES.pageNotFound;
                case this.orderIsNotAvailableMobile:
                    return ERROR_CODES.isNotAvailableOnMobile;
                default:
                    return null;
            }
        },

        isPageHasErrors() {
            return this.pageNotFound || this.orderIsNotAvailableMobile;
        },

        ...mapState({
            userHasAccess: (state) => state.access.userHasAccess,
            isLoadingEnd: (state) => !state.access.isAnyRequestLoading,
        }),
    },

    methods: {
        async logout() {
            this.clearAccess();
            await this.$store.dispatch('catalogClearAll');
            this.$store.commit('resetUser');
            keycloak.logout();
        },

        async showModalOrderVersion() {
            await this.$bvModal.msgBoxOk(this.$t('main.modalReload.message'), {
                title: this.$t('main.modalReload.title'),
                okTitle: this.$t('main.modalReload.ok'),
                okVariant: 'success',
                hideHeaderClose: false,
                centered: true,
                headerCloseContent: '',
                headerClass: 'p-0 pt-3 pb-3',
                bodyClass: 'p-0 pb-1 pt-1',
                footerClass: 'p-0 pb-4 pt-3',
            });

            sessionStorage.removeItem('isVisibleModalReload');
        },
    },

    watch: {
        $route: function() {
            this.pageNotFound = false;
            this.orderIsNotAvailableMobile = false;
            this.h1 = null;
            this.mainSubtitle = '';
        },
    },

    mounted() {
        this.$root.$on(
            'pageNotFound',
            (pageNotFound) => {
                this.pageNotFound = pageNotFound;
            },
        );

        this.$root.$on(
            'orderIsNotAvailableMobile',
            (orderIsNotAvailableMobile) => {
                this.orderIsNotAvailableMobile = orderIsNotAvailableMobile;
            },
        );

        this.$root.$on(
            'h1',
            (text) => {
                this.h1 = text;
            },
        );
        this.$root.$on(
            'setMainSubtitle',
            (text) => {
                this.mainSubtitle = text;
            },
        );

        if (sessionStorage.getItem('isVisibleModalReload')) {
            this.showModalOrderVersion();
        }
    },
};
</script>

<style lang="scss">
    $colorRed: red !important;
    $colorBlue: blue !important;
    $colorPrimary: #0A54CC;
    $textColor: #303030;
    $mainPadding: 0 64px;

    $borderColor: #ced4da;

    a {
        color: $colorPrimary;
    }

    .text-decoration-underline {
        text-decoration: underline!important;
    }

    .text-primary {
        color: $colorPrimary !important;
    }

    .container-main {
        padding: $mainPadding;
    }

    .bg-light {
        color: $textColor!important;
        background-color: #F6F7F7!important;
    }

    .bg-purple {
        color: $textColor!important;
        background-color: #FF00FF!important;
    }

    .nav-link {
        color: $textColor!important;
    }

    .icon {
        background-color: currentColor;
        display: block;
        -webkit-mask: url(../public/img/icons.svg) no-repeat;
        mask: url(../public/img/icons.svg) no-repeat;
        -webkit-mask-size: 52em 34em;
        mask-size: 52em 34em;
        width: 1.5em;
        height: 1.5em;

        &.icon-menu {
            mask-position: 26.25% 3.75%;
        }

        &.icon-file {
            mask-position: 32.25% 22.5%;
        }

        &.icon-renew {
            mask-position: 85.65% 13%;
        }

        &.icon-sort {
            mask-position: 67.85% 13%;
        }

        &.icon-settings {
            mask-position: 26.25% 68.5%;
        }

        &.icon-truck {
            mask-position: 38.1% 68.5%;
        }

        &.icon-sliders {
            mask-position: 50.15% 49.75%;
        }

        &.icon-users {
            mask-position: 62.1% 77.75%;
        }

        &.icon-tool {
            mask-position: 8.75% 95.75%;
        }

        &.icon-logout {
            mask-position: 73.65% 13%;
        }

        &.icon-share {
            mask-position: 91.55% 13%;
        }

        &.icon-key {
            mask-position: 15.1% 96.05%;
        }

        &.icon-support {
            mask-position: 19.65% 85.5%;
        }

        &.icon-sync {
            mask-position: 79.7% 13%;
        }

        &.icon-check {
            mask-position: 8.4% 3.75%;
        }

        &.icon-mail {
            mask-position: 44.05% 22.5%;
        }

        &.icon-calendar {
            mask-position: 2.5% 59.25%;
        }

        &.icon-corner-up-right {
            mask-position: 21.25% 96.05%;
        }

        &.icon-clock {
            mask-position: 55.95% 31.55%;
        }

        &.icon-arrows-alt {
            mask-position: 32.25% 96.75%;
        }

        &.icon-lock {
            mask-position: 55.9% 22.25%;
        }

        &.icon-times {
            mask-position: 2.5% 3.75%;
        }

        &.icon-eye {
            mask-position: 73.75% 22.25%;
        }

        &.icon-eye-slash {
            mask-position: 79.7% 22.25%;
        }

        &.icon-trash {
            mask-position: 67.83% 22.25%;
        }

        &.icon-trash-times {
            mask-position: 61.89% 22.25%;
        }

        &.icon-clock {
            mask-position: 55.9% 31.5%;
        }

        &.icon-edit {
            mask-position: 14.35% 22.25%;
        }

        &.icon-pencil {
            mask-position: 2.5% 22.25%;
        }

        &.icon-copy {
            mask-position: 2.48% 96.2%;
        }

        &.icon-box {
            mask-position: 26.25% 50%;
        }

        &.icon-label {
            mask-position: 26.5% 96.75%;
        }

        &.icon-filter {
            mask-position: 55.4% 50%;
        }

        &.icon-phone {
            mask-position: 67.55% 50%;
        }

        &.icon-check-circle {
            mask-position: 26.25% 31.5%;
        }

        &.icon-times-circle {
            mask-position: 14.35% 31.5%;
        }

        &.icon-question-circle {
            mask-position: 38.13% 31.55%;
        }

        &.icon-search {
            mask-position: 38.0% 3%;
        }

        &.icon-message {
            mask-position: 20.25% 21.75%;
        }

        &.icon-report {
            mask-position: 14.35% 68.25%;
        }

        &.icon-alert {
            mask-position: 20.35% 68.7%;
        }

        &.icon-plus {
            mask-position: 14.3% 3.75%;
        }

        &.icon-minus {
            mask-position: 20.3% 3.75%;
        }

        &.icon-info {
            mask-position: 32.3% 31.5%;
        }
    }

    .table {
        .table-variant .table tbody tr td:first-of-type {
            width: 3px;
        }

        & .order-item-main {
            min-width: 8rem;
        }

        & .color-row {
            height: 3rem;
            width: 3px;
        }

        & .table- {
            &task- {
                &online- {
                    &no-delivery {
                        background-color: #fef601;
                    }
                }

                &confirming {
                    background-color: #ffb7b7;
                }
            }

            &formed-, &to-formation- {
                &online- {
                    &no-delivery {
                        background-color: #fef601 !important;
                    }
                }
            }

            &disbandment- {
                color: $colorRed;

                &secondary {
                    color: $colorRed;
                }

                &completed {
                    color: $colorRed;
                }

                &waiting {
                    color: $colorRed;
                }
            }

            &on-disbanding, &disbandment-on-disbanding {
              background-color: #EBEBEB !important;
                & .color-row {
                    background-color: #B1B1B1 !important;
                }
                & .btn {
                    background-color: #EBEBEB !important;
                    border-color: #EBEBEB !important;
                }
            }

            &issued, &disbandment-issued {
              background-color: #B9FCD5 !important;
                & .color-row {
                    background-color: #00B94D !important;
                }
                & .btn {
                    background-color: #B9FCD5 !important;
                    border-color: #B9FCD5 !important;
                }
            }

            &closed, &disbandment-closed {
              background-color: #D6D6D6 !important;
                & .color-row {
                    background-color: #A9A9A9 !important;
                }
                & .btn {
                    background-color: #D6D6D6 !important;
                    border-color: #D6D6D6 !important;
                }
            }

            &completed, &disbandment-completed {
              background-color: #fef9e7 !important;
                & .color-row {
                  background-color: #FFF155 !important;
                }
                & .btn {
                    background-color: #fef9e7 !important;
                    border-color: #fef9e7 !important;
                }
            }

            &waiting {
                & .color-row {
                    background-color: #FFFFFF !important;
                }
            }

            &disbandment-waiting {
                & .color-row {
                    background-color: $colorRed;
                }
            }

            &changed, &disbandment-changed {
              background-color: #FFC4CE !important;
                & .color-row {
                  background-color: #F20034 !important;
                }
                & .btn {
                    background-color: #FFC4CE !important;
                    border-color: #FFC4CE !important;
                }
            }

            &waiting-to-formation {
                color: $colorBlue;
                & .color-row {
                    color: $colorBlue;
                }
            }

            &priority {
                background-color: #f45858 !important;
                & .color-row {
                    background-color: #FE9494 !important;
                }

                & .btn {
                    background-color: #f45858 !important;
                    border-color: #f45858 !important;
                }
            }
        }

        & td, & th {
            border-bottom: 1px solid #dee2e6;
            border-top: 0;
        }
        &.b-table > thead > tr, &.b-table > tfoot > tr {
            & > [aria-sort] {
                background-size: auto;
            }
            & > [aria-sort=none] {
                background-image: url(../public/img/sort.svg);
            }
            & > [aria-sort=ascending] {
                background-image: url(../public/img/sort-asc.svg);
            }
            & > [aria-sort=descending] {
                background-image: url(../public/img/sort-desc.svg);
            }
        }
        &.table-borderless {
            & td, & th {
                border-bottom: 0;
                border-top: 0;
            }
        }
    }
    @media (min-width: 576px) {
        .modal-sm {
            max-width: 302px;
        }
    }
    @media (min-width: 960px) {
        .sidebar {
            & > .col-md-1 {
                max-width: (12/96)*100% !important;
                flex: (12/96)*100% !important;
            }
            & > .col-md-2 {
                max-width: (32/96)*100% !important;
                flex: (32/96)*100% !important;
            }
            & > .col-md-10 {
                max-width: (64/96)*100% !important;
                flex: (64/96)*100% !important;
            }

            & > .col-md-11 {
                max-width: (84/96)*100% !important;
                flex: (84/96)*100% !important;
            }
        }
    }

    @media (min-width: 1200px) {
        .modal-xl {
            max-width: 1200px;
        }
        .sidebar {
            & > .col-md-1 {
                max-width: (10/96)*100% !important;
                flex: (10/96)*100% !important;
            }
            & > .col-md-2 {
                max-width: (24/96)*100% !important;
                flex: (24/96)*100% !important;
            }
            & > .col-md-10 {
                max-width: (72/96)*100% !important;
                flex: (72/96)*100% !important;
            }

            & > .col-md-11 {
                max-width: (86/96)*100% !important;
                flex: (86/96)*100% !important;
            }
        }
    }
    @media (min-width: 1350px) {
        .sidebar {
            & > .col-md-1 {
                max-width: (8/96)*100% !important;
                flex: (8/96)*100% !important;
            }
            & > .col-md-2 {
                max-width: (20/96)*100% !important;
                flex: (20/96)*100% !important;
            }
            & > .col-md-10 {
                max-width: (76/96)*100% !important;
                flex: (76/96)*100% !important;
            }

            & > .col-md-11 {
                max-width: (88/96)*100% !important;
                flex: (88/96)*100% !important;
            }
        }
    }

    .cursor-pointer {
        cursor: pointer;
    }

    .disable-user-select {
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
    }

    .nowrap {
        white-space: nowrap;
    }

    ::-webkit-scrollbar {
        width: 6px;
        height: 6px;
    }

    ::-webkit-scrollbar-track {
        background: white;
    }

    ::-webkit-scrollbar-thumb {
        background-color: #9E9E9E;
    }

    .modal-content {
        min-height: 10em;
    }
    .confirm-footer {
        padding: 0;
        position: relative;
        min-height: 8em;
        & button {
            padding: 0.5rem!important;
            position: absolute;
            top: 0;
            &:first-of-type {
                top: auto;
                bottom: 1em;
            }
        }
    }

    .custom-select {
        -webkit-appearance: none;
        -moz-appearance: none;
        -ms-appearance: none;
        appearance: none;
        outline: 0;
        background: #fff url('../public/img/chevron-down.svg') right 0.75rem center/13px 13px no-repeat;

        &.disabled {
            color: #74747A;
        }
    }

    .form-control, .custom-select {
        font-size: 13px;
        height: 3rem;
        border-color: #E0E0E0;

        &:focus::-webkit-input-placeholder
        {
            line-height: 20px;
            transition: line-height 0.5s ease;
        }

        &.form-product {
            height: auto;
        }
    }

    #app {
        font-family: 'Inter', Helvetica, Arial, sans-serif;
        font-style: normal;
        font-weight: 500;
        font-size: 13px;
        line-height: 16px;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        color: #2c3e50;
        margin-top: 0;

        ul.pagination {
            & li.page-item {
                margin-left: 0.5rem!important;

                &.active {
                    .page-link {
                        color: #fff;
                        background-color: #007BFF;

                        &:hover {
                            background-color: #0069D9;
                        }

                        &:focus:not(:hover):not(:active) {
                            background-color: #007BFF;
                        }

                        &:active {
                            background-color: #0062CC;
                            border-color: #80BDFF;
                            box-shadow: 0 0 0 2px #007BFF40;
                        }
                    }
                }

                .page-link {
                    border-radius: 0.25rem!important;
                    color: #212529;
                    background-color: #f8f9fa;
                    border-color: #f8f9fa;
                    border-width: 1px;
                    transition: all .15s ease-in-out;
                    line-height: 1.21;

                    &:hover {
                        background-color: #E2E6EA;
                    }

                    &:focus:not(:hover):not(:active) {
                        background-color: #f8f9fa;
                    }

                    &:focus {
                        box-shadow: none;
                    }

                    &:active {
                        background-color: #DAE0E5;
                        border-color: #D3D9DF;
                        box-shadow: 0 0 0 2px #D3D9DF80;
                    }
                }
            }
        }

        & .pre {
            white-space: pre;
        }

        & .mx-datepicker {
            & .mx-input-wrapper {
                & .mx-input {
                    height: 38px;
                }

                & .mx-input-append {
                    top: 5px;
                    & .mx-calendar-icon {
                        height: auto;
                    }
                }
            }
        }

        .list-table {
            & th {
                border-bottom-width: 1px;
                vertical-align: middle;
            }
            & td {
                vertical-align: middle;
            }
        }

        & .form-button {
            height: 3rem;
            min-width: 10em;
        }

        & .list-buttons {
            overflow-x: hidden;

            &::-webkit-scrollbar-thumb {
                background-color: white;
                height: 20px;
            }
        }

        & .custom-alert {
            position: absolute;
            top: 2rem;
            left: 35%;
            background-color: #000;
            color: #fff;
            padding: 0.25rem 1.25rem;
            border-radius: 0.4rem;
            &.alert-danger {
                background-color: #DC3545;
            }
        }

        & .clear {
            min-height: 10em;
        }
    }
    .btn {
        font-size: 13px;
        &.btn-success {
            background-color: #81BB3C;
            border-color: #81BB3C;

            &.disabled {
                border-color: #EBEBEB!important;
                background-color: #EBEBEB!important;
                color: #9E9E9E!important;
            }
        }
        &.btn-light {
            color: $colorPrimary;
        }
        &.btn-outline-secondary {
            border-color: $borderColor;
        }
        &.event {
            width: 2.16em;
            aspect-ratio: 1;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0;
            &,
            &:focus,
            &:active {
                &:not(:disabled):not(.disabled) {
                    border: 0.0625rem solid #6C757D !important;
                    box-shadow: none !important;
                    border-radius: 0.375rem !important;
                    color: #6C757D !important;
                    background-color: transparent !important;

                    &:hover:not(.start-counter) {
                        background-color: #6C757D !important;
                        color: #FFFFFF !important;
                    }

                    &.start-counter {
                        box-shadow: 0 0 0.125rem 0 #FEC200 !important;
                        font-weight: 500;
                        border: none !important;
                        cursor: default;
                    }
                }
            }
        }
        &.btn-secondary {
            color: #000;
            background-color: #fff;
            border-color: #fff;

            &:active {
                color: #000!important;
                background-color: #fff!important;
                border-color: #fff!important;
            }
        }
    }

    .modal-content {
        padding: 0 2em;
        font-size: 13px;

        &.modal-min {
            max-width: 24em;
        }
    }
    .modal-header {
        border-bottom: 0;
        & .close {
            position: absolute;
            right: -1em;
            color: #fff;
            top: 0.6em;
            background: url(../public/img/times.svg) no-repeat;
        }
    }
    .modal-footer {
        border-top: 0;

        & .btn {
            width: 100%;
        }

        &>* {
            margin: 0;
        }
    }

    .fade-enter-active, .fade-leave-active {
        transition: opacity .5s;
    }

    .fade-enter, .fade-leave-to {
        opacity: 0;
    }
    .nav-link {
        &.active {
            background-color: #F6F7F7 !important;
        }
    }

    .form-element {
       line-height: 1.5rem;
    }

    .custom-control {
       line-height: 1.5rem;
    }

    .form-middle {
       line-height: 3rem;
    }

    .custom-checkbox .custom-control-label::before {
        border-radius: 0.1rem;
    }

    .action-button .btn {
        min-width: 7rem;
        line-height: 1.2;
        text-align: center;
    }

    .toggle-info {
        color: #0A54CC!important;
        &:focus {
            color: #0A54CC!important;
        }
    }

    .block-field {
        padding-top: 0.75rem;
        padding-bottom: 0.75rem;
    }

    .help {
        position: absolute;
        top: 1rem;
        right: 0;
    }

    .support {
        position: fixed;
        bottom: 1rem;
        right: 1rem;
    }

    .main-subtitle {
        display: none;
    }

@media(max-width: 767px) {
    .container-main {
        padding: 0 16px;
    }

    .content {
        padding-top: 70px;
    }

    .list-table {
        th,
        td {
            padding: 18.6px 0;
        }
        th:first-child {
            padding: 0 0 0 12px;
        }
        td:first-child {
            padding: 18.6px 9px;
        }
    }

    .table {
        .color-row {
            width: 4px;
            height: 38px;
        }
    }

    .heading strong {
        font-size: 24px;
        font-weight: 600;
    }

    .main-subtitle {
        display: block;
        font-size: 13px;
        font-weight: 600;
        line-height: 18px;
        margin-top: 8px
    }
}
</style>